import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Users, DollarSign, AlertTriangle, Calendar, CheckCircle, XCircle, ClipboardList, Clock, Phone, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';

const getTimeElapsed = (dateStr) => {
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now - created;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return { text: `${days}d ${remHours}h`, isOverdue: true };
  }
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return { text: `${hours}h ${minutes}m`, isOverdue: false };
};

const StatCard = ({ title, value, color, subtext }) => (
  <div className={`bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100 border-l-4 ${color.replace('bg-', 'border-l-')}`}>
    <p className="text-xs sm:text-sm text-gray-500 mb-1 truncate">{title}</p>
    <h3 className="text-xl sm:text-2xl font-bold text-gray-800">{value}</h3>
    {subtext && <p className="text-[10px] sm:text-xs text-gray-400 mt-1 truncate">{subtext}</p>}
  </div>
);

const OfficerDashboard = () => {
  const [stats, setStats] = useState(null);
  const [expiringToday, setExpiringToday] = useState([]);
  const [expiringTomorrow, setExpiringTomorrow] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [uncollected, setUncollected] = useState([]);
  const [missedRenewals, setMissedRenewals] = useState([]);
  const [missedExpanded, setMissedExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTechnician, setIsTechnician] = useState(false);

  const navigate = useNavigate();
  const perms = usePermissions();

  // Helper: true unless admin has explicitly disabled it
  const can = (key) => perms[key] !== false;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [res, uncollectedRes, meRes] = await Promise.all([
          api.get('/officers/dashboard-stats/'),
          api.get('/officers/uncollected/'),
          api.get('/users/me/'),
        ]);
        const data = res.data;

        setStats({ activeCount: data.active_count, todayCollections: data.today_collections });
        setExpiringToday(data.expiring_today || []);
        setExpiringTomorrow(data.expiring_tomorrow || []);
        setComplaints(data.recent_complaints || []);
        setUncollected(uncollectedRes.data || []);
        setMissedRenewals(data.missed_renewals_list || []);
        setIsTechnician((meRes.data?.role || '') === 'officer_technician');
      } catch (error) {
        console.error("Error fetching officer stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const openComplaints = isTechnician ? complaints.filter(c => c.status === 'open').length : 0;
  const inProgressComplaints = isTechnician ? complaints.filter(c => c.status === 'in_progress').length : 0;

  if (loading) return <div className="p-8 text-center">Loading Dashboard...</div>;

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">

      {/* Stats Grid */}
      {can('show_dashboard_stats') && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatCard title="Active Customers" value={stats.activeCount} color="bg-blue-500" />
          <StatCard title="Collected Today" value={`PKR ${stats.todayCollections.toLocaleString()}`} color="bg-green-500" />
          {can('show_today_expiring') && <StatCard title="Expiring Today" value={expiringToday.length} color="bg-red-500" subtext="Action required" />}
          {can('show_missed_renewals') && <StatCard title="Missed Renewals" value={missedRenewals.length} color="bg-rose-600" subtext="Not renewed this month" />}
          {isTechnician && (
            <>
              {can('show_uncollected') && <StatCard title="Uncollected" value={uncollected.length} color="bg-orange-500" subtext="No collection entry" />}
              <StatCard title="Open Complaints" value={openComplaints} color="bg-blue-500" />
              <StatCard title="In Progress" value={inProgressComplaints} color="bg-yellow-500" />
            </>
          )}
        </div>
      )}

      {/* Missed Renewals This Month */}
      {can('show_missed_renewals') && missedRenewals.length > 0 && (
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-rose-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setMissedExpanded(!missedExpanded)}
              className="w-full p-4 bg-rose-50 border-b border-rose-100 flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle size={18} className="text-rose-800" />
                <h2 className="font-bold text-rose-800">Missed Renewals — This Month</h2>
                <span className="bg-rose-200 text-rose-800 text-xs px-2 py-1 rounded-full font-bold">
                  {missedRenewals.length} Customers
                </span>
              </div>
              <ChevronDown size={18} className={`text-rose-800 transition-transform ${missedExpanded ? 'rotate-180' : ''}`} />
            </button>
            {missedExpanded && (
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="p-3">Customer</th>
                    <th className="p-3">Phone</th>
                    <th className="p-3 hidden sm:table-cell">Package</th>
                    <th className="p-3 hidden md:table-cell">Area</th>
                    <th className="p-3">Expiry</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {missedRenewals.map(c => (
                    <tr key={c.id} className="border-b border-gray-50 hover:bg-rose-50/40">
                      <td className="p-3 cursor-pointer" onClick={() => navigate(`/customers/${c.id}/history`)}>
                        <div className="font-bold text-gray-800 hover:text-blue-600">{c.username}</div>
                        <div className="text-xs text-gray-500">{c.name}</div>
                      </td>
                      <td className="p-3">
                        {c.phone ? (
                          <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium text-xs">
                            <Phone size={12} />{c.phone}
                          </a>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="p-3 text-xs text-gray-600 hidden sm:table-cell">{c.package_name || '—'}</td>
                      <td className="p-3 text-xs text-gray-600 hidden md:table-cell">{c.area_name || '—'}</td>
                      <td className="p-3 text-xs text-rose-600 font-medium">{c.expiry_date}</td>
                      {can('can_add_collections') && (
                        <td className="p-3 text-right">
                          <button
                            onClick={() => navigate(`/collections/new?customer=${c.id}`)}
                            className="text-xs bg-rose-600 text-white px-2 py-1 rounded hover:bg-rose-700"
                          >
                            Collect
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Uncollected This Month — technician only */}
      {isTechnician && can('show_uncollected') && uncollected.length > 0 && (
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden">
            <div className="p-4 bg-orange-50 border-b border-orange-100 flex justify-between items-center">
              <h2 className="font-bold text-orange-800 flex items-center gap-2">
                <AlertTriangle size={18} /> Uncollected This Month
              </h2>
              <span className="bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded-full font-bold">
                {uncollected.length} Users
              </span>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="p-3">User</th>
                    <th className="p-3">Package</th>
                    <th className="p-3 hidden sm:table-cell">Expiry</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {uncollected.slice(0, 10).map(c => (
                    <tr key={c.id} className="border-b border-gray-50 hover:bg-orange-50/50">
                      <td className="p-3 cursor-pointer" onClick={() => navigate(`/customers/${c.id}/history`)}>
                        <div className="font-bold text-gray-800 hover:text-blue-600">{c.username}</div>
                        <div className="text-xs text-gray-500">{c.name}</div>
                      </td>
                      <td className="p-3 text-xs">{c.package_name}</td>
                      <td className="p-3 text-xs text-red-600 font-medium hidden sm:table-cell">{c.expiry_date}</td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => navigate(`/collections/new?customer=${c.id}`)}
                          className="text-xs bg-orange-600 text-white px-2 py-1 rounded hover:bg-orange-700"
                        >
                          Collect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Recent Complaints — technician only */}
      {isTechnician && complaints.length > 0 && (
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="font-bold text-gray-800 flex items-center gap-2">
                <ClipboardList size={18} className="text-gray-400" /> My Complaints
              </h2>
              <button
                onClick={() => navigate('/technician/complaints')}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                View All →
              </button>
            </div>
            <div className="divide-y divide-gray-100">
              {complaints.slice(0, 5).map(c => {
                const time = getTimeElapsed(c.created_at);
                const statusColors = {
                  open: 'bg-blue-100 text-blue-700',
                  in_progress: 'bg-yellow-100 text-yellow-700',
                  resolved: 'bg-green-100 text-green-700',
                  closed: 'bg-gray-100 text-gray-700',
                };
                return (
                  <div key={c.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="font-medium text-gray-900 text-sm truncate">{c.title}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[c.status]}`}>
                          {c.status.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{c.customer_name} - {c.area_name || 'N/A'}</p>
                    </div>
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${time.isOverdue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {time.isOverdue && <AlertTriangle size={12} />}
                      <Clock size={12} />
                      {time.text}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Expiring Customers Section */}
      {(can('show_today_expiring') || can('show_tomorrow_expiring')) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Expiring Today */}
        {can('show_today_expiring') && <div className="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden">
          <div className="p-4 bg-red-50 border-b border-red-100 flex justify-between items-center">
            <h2 className="font-bold text-red-800 flex items-center gap-2">
              <Calendar size={18} /> Expiring Today
            </h2>
            <span className="bg-red-200 text-red-800 text-xs px-2 py-1 rounded-full font-bold">
              {expiringToday.length} Users
            </span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {expiringToday.length === 0 ? (
              <p className="p-4 text-center text-gray-500 text-sm">No expiries today.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="p-3">User</th>
                    <th className="p-3">Package</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {expiringToday.map(c => (
                    <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className={`p-3 ${can('can_view_customer_details') ? 'cursor-pointer' : ''}`} onClick={() => can('can_view_customer_details') && navigate(`/customers/${c.id}/history`)}>
                        <div className="font-bold text-gray-800 hover:text-blue-600">{c.username}</div>
                        <div className="text-xs text-gray-500">{c.name}</div>
                      </td>
                      <td className="p-3 text-xs">{c.package_name}</td>
                      <td className="p-3 text-right">
                        {c.paid_this_month ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-bold">PAID</span>
                        ) : can('can_add_collections') ? (
                          <button
                            onClick={() => navigate(`/collections/new?customer=${c.id}`)}
                            className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700"
                          >
                            Collect
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>}

        {/* Expiring Tomorrow */}
        {can('show_tomorrow_expiring') && <div className="bg-white rounded-xl shadow-sm border border-orange-100 overflow-hidden">
          <div className="p-4 bg-orange-50 border-b border-orange-100 flex justify-between items-center">
            <h2 className="font-bold text-orange-800 flex items-center gap-2">
              <Calendar size={18} /> Expiring Tomorrow
            </h2>
            <span className="bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded-full font-bold">
              {expiringTomorrow.length} Users
            </span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {expiringTomorrow.length === 0 ? (
              <p className="p-4 text-center text-gray-500 text-sm">No expiries tomorrow.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="p-3">User</th>
                    <th className="p-3">Package</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {expiringTomorrow.map(c => (
                    <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className={`p-3 ${can('can_view_customer_details') ? 'cursor-pointer' : ''}`} onClick={() => can('can_view_customer_details') && navigate(`/customers/${c.id}/history`)}>
                        <div className="font-bold text-gray-800 hover:text-blue-600">{c.username}</div>
                        <div className="text-xs text-gray-500">{c.name}</div>
                      </td>
                      <td className="p-3 text-xs">{c.package_name}</td>
                      <td className="p-3 text-right">
                        {c.paid_this_month ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-bold">PAID</span>
                        ) : can('can_add_collections') ? (
                          <button
                            onClick={() => navigate(`/collections/new?customer=${c.id}`)}
                            className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                          >
                            Remind
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>}

      </div>
      )}
    </div>
  );
};

export default OfficerDashboard;
